/**
 * Subtle signed-in indicator + sign-out, rendered just above the footer.
 * REGISTERED sessions only (guests are also authed — they see nothing;
 * the whole point is showing which ACCOUNT the trip belongs to).
 *
 * Sign out clears the session; the auth core fires sessionCleared and the
 * instance provider self-heals into a fresh guest, so the widget keeps
 * working (chat + browsing) — just detached from the account.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useView } from '../context/ViewContext';
import logger from '../utils/logger';

const AccountBadge: React.FC = () => {
  const { t } = useTranslation();
  const { authState, sessionInfo, logout } = useAuth();
  const { setView } = useView();
  const [busy, setBusy] = useState(false);

  if (authState !== 'authenticated') return null;
  const who =
    sessionInfo?.firstName ||
    sessionInfo?.email ||
    t('auth.account', 'Account');

  const onSignOut = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await logout();
      setView('chat');
    } catch (e) {
      logger.warn('[account] logout failed', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wg-account">
      <span className="wg-account__who">{who}</span>
      <span className="wg-account__sep">·</span>
      <button
        type="button"
        className="wg-account__signout"
        onClick={onSignOut}
        disabled={busy}
      >
        {t('auth.sign_out', 'Sign out')}
      </button>
    </div>
  );
};

export default AccountBadge;
